import * as React from 'react'
import { Plus, CheckCircle } from '@phosphor-icons/react'
import { toast } from 'react-hot-toast'
import { ConfirmationModal } from './confirmation-modal'
import { EditableScaleCard } from './editable-scale-card'
import { ListingScaleCard } from './listing-scale-card'
import { ToggleSwitch } from './toggle-switch'
import './custom-rating-scales.scss'
import {
  ABOVE_EXTRA_VALUES,
  BELOW_EXTRA_VALUES,
  buildInitialSavedScales,
  buildNewAboveScale,
  buildNewBelowScale,
  cloneScales,
  compactExtraScales,
  convertBackendToFrontend,
  convertFrontendToBackend,
  normalizeBoolean,
  normalizeColor,
  trimScales,
  validateScales,
} from './utils'
import {
  BackendScale,
  CustomRatingScalesPageProps,
  FieldErrors,
  RatingScale,
} from './types'
import { useApiClient } from '../../utils/use-api-client'
import { brandName } from 'app/packs/src/utils/brand'

const showSuccessToast = (message: string) =>
  toast(
    () => (
      <div className="flex items-center gap-2">
        <CheckCircle size={24} color="#39C674" weight="fill" />
        <span>{message}</span>
      </div>
    ),
    {
      position: 'bottom-center',
      style: {
        background: '#1E2532',
        color: '#fff',
        borderRadius: '8px',
        padding: '12px 16px',
      },
    },
  )

export const CustomRatingScalesPage: React.FC<CustomRatingScalesPageProps> = ({
  orgColor = '2E9E5D',
  initialEnabled = true,
  orgSlug,
  initialScaleId,
  initialScalesData,
}) => {
  const apiClient = useApiClient()

  const resolvedOrgColor = React.useMemo(
    () => normalizeColor(orgColor),
    [orgColor],
  )

  const [isEnabled, setIsEnabled] = React.useState(
    normalizeBoolean(initialEnabled, true),
  )
  const [isEditing, setIsEditing] = React.useState(false)
  const [hasPersistedScale, setHasPersistedScale] =
    React.useState(!!initialScaleId)
  const [savedScales, setSavedScales] = React.useState<RatingScale[]>(() =>
    initialScalesData ? buildInitialSavedScales(initialScalesData) : [],
  )
  const [draftScales, setDraftScales] = React.useState<RatingScale[]>([])
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [removeTargetId, setRemoveTargetId] = React.useState<string | null>(
    null,
  )
  const [showDisableModal, setShowDisableModal] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)

  const apiBase = orgSlug
    ? `/api/v1/internal/orgs/${orgSlug}/custom_rating_scales`
    : null

  const sortedDraftScales = React.useMemo(
    () => [...draftScales].sort((a, b) => a.scaleValue - b.scaleValue),
    [draftScales],
  )

  const usedDraftValues = React.useMemo(
    () => new Set(draftScales.map((s) => s.scaleValue)),
    [draftScales],
  )

  const canAddBelow = BELOW_EXTRA_VALUES.some((v) => !usedDraftValues.has(v))
  const canAddAbove = ABOVE_EXTRA_VALUES.some((v) => !usedDraftValues.has(v))

  const openEditView = () => {
    const initialDraft = hasPersistedScale
      ? cloneScales(savedScales)
      : buildInitialSavedScales()
    setDraftScales(initialDraft)
    setErrors({})
    setSaveError(null)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setDraftScales([])
    setErrors({})
    setSaveError(null)
    setIsEditing(false)
  }

  const handleToggleClick = async () => {
    if (isEnabled) {
      setShowDisableModal(true)
      return
    }

    if (!apiBase) {
      setIsEnabled(true)
      return
    }

    try {
      await apiClient.patch(`${apiBase}/toggle_enable`, {
        org: { enable_custom_rating: true },
      })

      setIsEnabled(true)

      // Re-fetch the active scale so we don't show stale data from initial load.
      // The backend may have reset to the default scale when toggling off,
      // so we need the current server state rather than the cached savedScales.
      try {
        const scaleResponse = await apiClient.get<{
          data: BackendScale | null
        }>(apiBase)
        const freshLabels = scaleResponse?.data?.labels
        if (freshLabels && freshLabels.length > 0) {
          setSavedScales(convertBackendToFrontend(freshLabels))
          setHasPersistedScale(true)
        } else {
          setSavedScales([])
          setHasPersistedScale(false)
        }
      } catch {
        // Re-fetch failed — toggle is already enabled, stale UI data is acceptable
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to enable. Please try again.'
      setSaveError(message)
    }
  }

  const handleConfirmDisable = async () => {
    if (!apiBase) {
      setIsEnabled(false)
      setIsEditing(false)
      setShowDisableModal(false)
      setErrors({})
      setDraftScales([])
      return
    }

    try {
      const response = await apiClient.patch<{
        notice?: string
        enable_custom_rating?: boolean
      }>(`${apiBase}/toggle_enable`, {
        org: { enable_custom_rating: false },
      })
      setIsEnabled(false)
      setIsEditing(false)
      setShowDisableModal(false)
      setErrors({})
      setDraftScales([])
      showSuccessToast(response?.notice ?? 'Custom rating has been turned off.')
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to disable. Please try again.'
      setSaveError(message)
      setShowDisableModal(false)
    }
  }

  const updateDraftScale = (id: string, next: Partial<RatingScale>) => {
    setDraftScales((prev) =>
      prev.map((scale) => (scale.id === id ? { ...scale, ...next } : scale)),
    )

    setErrors((prev) => {
      if (!prev[id]) return prev
      return {
        ...prev,
        [id]: {
          ...prev[id],
          ...(next.label !== undefined ? { label: undefined } : {}),
          ...(next.description !== undefined ? { description: undefined } : {}),
        },
      }
    })
  }

  const handleAddBelowScale = () => {
    const newScale = buildNewBelowScale(draftScales)
    if (!newScale) return
    setDraftScales((prev) => [...prev, newScale])
  }

  const handleAddAboveScale = () => {
    const newScale = buildNewAboveScale(draftScales)
    if (!newScale) return
    setDraftScales((prev) => [...prev, newScale])
  }

  const persistScales = async (
    scales: RatingScale[],
    action: 'save' | 'remove' = 'save',
  ) => {
    const trimmed = trimScales(scales)

    if (!apiBase) {
      setSavedScales(trimmed)
      setHasPersistedScale(true)
      if (action === 'remove') {
        setDraftScales(trimmed)
      } else {
        setDraftScales([])
        setIsEditing(false)
      }
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      const response = await apiClient.post<{
        data: BackendScale
        notice?: string
      }>(apiBase, {
        custom_rating_scale: {
          labels: convertFrontendToBackend(trimmed),
          action,
        },
      })

      const freshScales = response?.data?.labels
        ? convertBackendToFrontend(response.data.labels)
        : trimmed

      setSavedScales(freshScales)
      setHasPersistedScale(true)
      if (action === 'remove') {
        setDraftScales(freshScales)
      } else {
        setDraftScales([])
        setIsEditing(false)
      }
      if (response?.notice) showSuccessToast(response.notice)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save. Please try again.'
      setSaveError(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSave = async () => {
    const nextErrors = validateScales(draftScales)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    await persistScales(draftScales, 'save')
  }

  const handleRemoveScale = async () => {
    if (!removeTargetId) return

    const newScales = draftScales.filter((scale) => scale.id !== removeTargetId)
    const isUnsaved = !savedScales.some((s) => s.id === removeTargetId)

    const compacted = compactExtraScales(newScales)

    if (isUnsaved) {
      setDraftScales(compacted)
      setErrors((prev) => {
        const next = { ...prev }
        delete next[removeTargetId]
        return next
      })
      setRemoveTargetId(null)
      showSuccessToast('Scale removed.')
      return
    }

    const nextErrors = validateScales(compacted)
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      setRemoveTargetId(null)
      return
    }

    setErrors((prev) => {
      const next = { ...prev }
      delete next[removeTargetId]
      return next
    })

    setRemoveTargetId(null)

    await persistScales(compacted, 'remove')
  }

  const actionButtonStyle = {
    backgroundColor: resolvedOrgColor,
    borderColor: resolvedOrgColor,
  }

  const addButton = (onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 text-[16px] font-medium leading-6 text-[#1D264A] hover:text-[#5E6580]"
    >
      <Plus size={20} />
      Add another rating scale
    </button>
  )

  return (
    <>
      <div className="custom-rating-scales-page max-w-[890px]">
        <section className="mb-12">
          <h4 className="mb-4 text-[19px] font-normal leading-6 text-[#5E6580]">
            Rating system
          </h4>

          <p className="mb-4 text-[16px] font-normal leading-6 text-[#1D264A]">
            {brandName()} check-in 3-scale ratings (Working Towards, Meeting,
            and Exceeding) are defaulted for all organizations. To customize
            your ratings, click the Custom rating configuration toggle below.
          </p>

          <div className="mb-7 rounded-[12px] border border-[#D9DDE7] bg-white px-5 py-5">
            <div className="mb-2 flex items-center justify-between gap-5">
              <h5 className="mb-0 text-[16px] font-medium leading-6 text-[#1D264A]">
                Custom rating configuration
              </h5>

              <ToggleSwitch
                checked={isEnabled}
                onClick={handleToggleClick}
                activeColor={resolvedOrgColor}
                ariaLabel="Custom rating configuration"
              />
            </div>

            <p className="mb-0 text-[16px] font-normal leading-6 text-[#5E6580]">
              {isEnabled
                ? 'Allow check-in ratings to use and use up to 7 a customizable scale with editable labels.'
                : 'Allow check-in ratings to use a customizable scale with editable labels.'}
            </p>
          </div>

          {isEnabled && (
            <>
              <div className="mb-6 flex items-center justify-between gap-4">
                <h4 className="mb-0 text-[19px] font-normal leading-6 text-[#5E6580]">
                  Custom rating
                </h4>

                {isEditing ? (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="inline-flex h-[48px] items-center justify-center rounded border border-[#1D264A] px-6 text-[16px] font-medium text-[#1D264A] hover:bg-[#F7F8FB] disabled:opacity-50"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={isSaving}
                      className="inline-flex h-[48px] items-center justify-center rounded border px-6 text-[16px] font-medium text-white disabled:opacity-50"
                      style={actionButtonStyle}
                    >
                      {isSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={openEditView}
                    className="inline-flex h-[48px] items-center justify-center rounded border px-6 text-[16px] font-medium text-white"
                    style={actionButtonStyle}
                  >
                    {hasPersistedScale ? 'Edit' : 'Configure'}
                  </button>
                )}
              </div>

              {saveError && (
                <p className="mb-4 text-sm text-red-600">{saveError}</p>
              )}

              {isEditing ? (
                <>
                  {canAddBelow && (
                    <div className="mb-6">{addButton(handleAddBelowScale)}</div>
                  )}

                  <div className="space-y-6">
                    {sortedDraftScales.map((scale, index) => (
                      <EditableScaleCard
                        key={scale.id}
                        scale={scale}
                        scaleIndex={index + 1}
                        errors={errors[scale.id]}
                        onChange={(next) => updateDraftScale(scale.id, next)}
                        onRemove={() => setRemoveTargetId(scale.id)}
                      />
                    ))}
                  </div>

                  {canAddAbove && (
                    <div className="mt-6">{addButton(handleAddAboveScale)}</div>
                  )}
                </>
              ) : hasPersistedScale ? (
                <div className="space-y-6">
                  {[...savedScales]
                    .sort((a, b) => a.scaleValue - b.scaleValue)
                    .map((scale) => (
                      <ListingScaleCard key={scale.id} scale={scale} />
                    ))}
                </div>
              ) : (
                <p className="text-[16px] font-normal leading-6 text-[#5E6580]">
                  No custom rating scale configured yet. Click{' '}
                  <strong>Configure</strong> to set one up.
                </p>
              )}
            </>
          )}
        </section>
      </div>

      {removeTargetId && (
        <ConfirmationModal
          title="Are you sure you want to remove this scale?"
          description="Once the scale has been removed, you can not undo the action."
          confirmLabel="Remove"
          confirmVariant="danger"
          onClose={() => setRemoveTargetId(null)}
          onConfirm={handleRemoveScale}
        />
      )}

      {showDisableModal && (
        <ConfirmationModal
          title="Are you sure you want to turn off the custom rating?"
          description="The rating system will set back to default system for future check-ins."
          confirmLabel="Yes, turn off"
          confirmVariant="primary"
          onClose={() => setShowDisableModal(false)}
          onConfirm={handleConfirmDisable}
        />
      )}
    </>
  )
}
